import { FeeComponentService } from './../../../../api/fee-component.service';
import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import { FormService } from './../../../../api/form.service';
import SwalComponent from './../../../../utils/custom-swal.component';
import uuid from '../../../../utils/uuid';
import { MessageService } from './../../../../message.service';

@Component({
  selector: 'iv-non-standard-fee',
  templateUrl: './non-standard-fee.component.html',
  styleUrls: ['./non-standard-fee.component.scss']
})
export class NonStandardFeeComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('warnAlert', { static: true }) private warnAlert: SwalComponent;

  @Input() transaction;
  @Input() feeComponentIndex;
  @Input() currencySymbol;
  @Input() header;
  @Input() subheader;
  @Input() showValidation = false;

  @Output() saveFeeComponent = new EventEmitter();
  @Output() deleteFeeComponent = new EventEmitter();
  @Output() emitIncentiveFee = new EventEmitter();

  feeComponent = {
    id: 0,
    transaction_amount: 0,
    fee_structure: 0,
    amount: 0,
    additional_fee: 0,
    notes: ''
  };

  nonStandardFeeForm;

  $nonStandardFee;
  $nonStdFeeObservable;

  uuid = uuid();

  nonStandardFeeComponentsHolder;
  swalOptions: any;

  constructor(
    private fb: UntypedFormBuilder,
    private formService: FormService,
    private messageService: MessageService,
    private feeComponentService: FeeComponentService) {
      this.nonStandardFeeComponentsHolder = this.feeComponentService.nonStandardFeeComponentsHolder;
      this.swalOptions = this.getSwalOptions();
  }

  ngOnInit() {
    this.$nonStdFeeObservable = this.feeComponentService.$nonStandardFeeComponentsObservable.subscribe(result => {
      this.feeComponent = this.feeComponentService.nonStandardFeeComponentsHolder.nonStandardFeeComponents[this.feeComponentIndex];
      this.invokeSetupNonStandardFeeForm();
    });
  }

  ngOnDestroy() {
    if (this.$nonStandardFee) {
      this.$nonStandardFee.unsubscribe();
    }
    if (this.$nonStdFeeObservable) {
      this.$nonStdFeeObservable.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.feeComponent = this.feeComponentService.nonStandardFeeComponentsHolder.nonStandardFeeComponents[this.feeComponentIndex];
    this.invokeSetupNonStandardFeeForm();
  }

  invokeSetupNonStandardFeeForm() {
    if (this.feeComponent) {
      this.setupNonStandardFeeForm();
    }
  }

  setupNonStandardFeeForm() {
    if (this.transaction.transaction_type === 'sale') {
      if (+this.feeComponent.transaction_amount > 0) {
        this.feeComponent.fee_structure =
          +(+this.feeComponent.amount * 100 / +this.feeComponent.transaction_amount).toFixed(2);
      } else {
        this.feeComponent.fee_structure = 0;
        this.feeComponent.amount = 0;
      }
    }
    this.nonStandardFeeForm = this.fb.group({
      transaction_amount: [this.feeComponent.transaction_amount, Validators.required],
      fee_structure: [this.feeComponent.fee_structure, Validators.required],
      amount: [this.feeComponent.amount, Validators.required,null ,{updateOn: 'blur'}],
      additional_fee: [this.feeComponent.additional_fee],
      notes: [this.feeComponent.notes]
    });

    this.formService.addLoadedForm(this.nonStandardFeeForm, this.uuid);

    if (this.$nonStandardFee) {
      this.$nonStandardFee.unsubscribe();
    }

    this.$nonStandardFee = this.nonStandardFeeForm.valueChanges
      .pipe(debounceTime(500))
      .subscribe(changes => {
        if (this.transaction.transaction_type === 'sale') {
          this.evaluateFeeAmounts(changes);
        }
        this.saveFeeComponent.emit({ ...this.feeComponent, ...changes });
        // Send message to subscribers via observable subject
        this.messageService.sendMessage({ model: 'transactions', type: 'PUT', title: 'Updating transaction', text: '' });
      });

    if (this.showValidation === true && this.nonStandardFeeForm) {
      this.formService.showFormValidations(this.nonStandardFeeForm);
    }
  }

  evaluateFeeAmounts(changes) {
    let calculatedAmount: any = undefined;
    let calculatedFeeStructure: any = undefined;

    if (+changes['transaction_amount'] !== +this.feeComponent.transaction_amount) {
      calculatedAmount = +changes['transaction_amount'] * +changes['fee_structure'] / 100;
      this.nonStandardFeeForm.patchValue({ 'transaction_amount': changes['transaction_amount'] });
      this.nonStandardFeeForm.patchValue({ 'amount': calculatedAmount });
    } else if (+changes['amount'] !== +this.feeComponent.amount) {
      calculatedFeeStructure = +changes['amount'] * 100 / +changes['transaction_amount'];
      if (isNaN(calculatedFeeStructure)) {
        calculatedFeeStructure = 0;
      }
    } else if (+changes['fee_structure'] !== +this.feeComponent.fee_structure) {
      calculatedAmount = +changes['transaction_amount'] * +changes['fee_structure'] / 100;
      if (isNaN(calculatedAmount)) {
        calculatedAmount = 0;
      }
    }

    if (+changes['transaction_amount'] === 0) {
      this.nonStandardFeeForm.patchValue({ 'fee_structure': 0 });
      this.nonStandardFeeForm.patchValue({ 'amount': 0 });
      changes['amount'] = 0;
      changes['fee_structure'] = 0;
    } else if (calculatedFeeStructure !== undefined) {
      changes['fee_structure'] = +calculatedFeeStructure.toFixed(2);
      this.nonStandardFeeForm.patchValue({ 'fee_structure': changes['fee_structure'] });
    } else if (calculatedAmount !== undefined) {
      changes['amount'] = +calculatedAmount.toFixed(2);
      this.nonStandardFeeForm.patchValue({ 'amount': changes['amount'] });
    }
  }

  deleteComponent() {
    this.deleteFeeComponent.emit(this.feeComponent.id);
    // Send message to subscribers via observable subject
    this.messageService.sendMessage({ model: 'transactions', type: 'PUT', title: 'Updating transaction', text: '' });
  }

  showDeleteAlert() {
    this.warnAlert.fire().then(response => {
      if (response.value) {
        this.deleteComponent();
      }
    });
  }

  saveIncentive(feeComp) {
    this.feeComponent = { ...this.feeComponent, ...feeComp };
    this.saveFeeComponent.emit(this.feeComponent);
  }

  savePerformanceFeeComponent(feeComp) {
    this.feeComponent = {...this.feeComponent, ...feeComp };
    this.saveFeeComponent.emit(this.feeComponent);
  }

  getSwalOptions() {
    // { showConfirmButton: true, showCloseButton: true, showCancelButton: true }
    const options = {
      showConfirmButton: true,
      showCloseButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      showCancelButton: true
    };
    return this.formService.getSwalOptions(options);
  }
}
